import { Editor } from "@tinymce/tinymce-react";
import { debounce, groupBy, maxBy } from "lodash";
import { Card } from "primereact/card";
import { useCallback, useContext, useState } from "react";
import { generateUnderwritingPdf } from "../../../api/routes";
import { ClientId, featureEnabled } from "../../../client-features";
import { useClientId } from "../../../ClientConfig";
import { useToast } from "../../../context-providers/Toast";
import { ThemeContext } from "../../../themes/Theme";
import { downloadBlob } from "../../../util";
import { SafeReportResponse } from "../Conversion";
import { useUpdateReport } from "../data";
import { addressesToPlottable } from "../map/MapModal";
import { findAddressesInRadius, parseCoverage } from "../map/util";
import { formatAbbreviatedMoney } from "../Money";
import { breakDownAddressFieldByTiv, capitalize } from "./util";

const generateInitialReferralValueGeneric = (
  specialtyPropertyInfo: SafeReportResponse,
  clientId: ClientId
) => {
  const report = specialtyPropertyInfo.report_json;
  const companyName = report.company_info.company_name;

  const addresses = specialtyPropertyInfo.report_json.sovs
    .filter((sov) => sov.is_enabled)
    .flatMap((sov) => sov.addresses);
  const totalTIV = addresses.reduce((total, address) => total + address.tiv, 0);
  const locationsOverTenMil = addresses.filter(
    (address) => address.tiv > 10_000_000
  ).length;

  const locationCount = featureEnabled(
    clientId,
    "location-count-not-include-unique-buildings"
  )
    ? Object.keys(groupBy(addresses, "premises_number")).length
    : addresses.length;
  const buildingOrLocation = featureEnabled(
    clientId,
    "location-count-not-include-unique-buildings"
  )
    ? "building"
    : "location";

  const addressesWithSquareFootage = addresses.filter(
    (address) => address.square_footage && address.square_footage !== ""
  );
  const squareFootageTiv = addressesWithSquareFootage.reduce(
    (total, address) => total + address.tiv,
    0
  );
  const totalSquareFootage = addressesWithSquareFootage.reduce(
    (total, address) =>
      total + Number.parseFloat(address.square_footage ?? "0"),
    0
  );
  const costPerFoot = squareFootageTiv / totalSquareFootage;

  const squareFootageString =
    totalSquareFootage > 0
      ? `<div>The TIV of buildings with square footage provided is ${formatAbbreviatedMoney(
          squareFootageTiv
        )} on ${formatAbbreviatedMoney(
          totalSquareFootage,
          false
        )} square footage for a cost of <b>${formatAbbreviatedMoney(
          costPerFoot
        )}</b> per square foot.</div>`
      : "";

  const riskClusters = findAddressesInRadius(
    addressesToPlottable(
      addresses,
      specialtyPropertyInfo.report_json.company_info.named_insured ??
        specialtyPropertyInfo.report_json.company_info.company_name
    ).plottables,
    { first: 10 }
  );
  const maxRiskCluster = maxBy(riskClusters, "tiv");
  const windzoneAndAggregationString = `${
    maxRiskCluster
      ? `<div>The greatest TIV aggregation is <b>${formatAbbreviatedMoney(
          maxRiskCluster.tiv
        )}</b> of TIV within a 10 mile radius spanning ${
          maxRiskCluster.included.length
        } ${buildingOrLocation}${
          maxRiskCluster.included.length === 1 ? "" : "s"
        }.</div><br>`
      : ""
  }`;

  const totalBuilding = addresses.reduce(
    (total, address) => total + parseCoverage(address.building_value),
    0
  );
  const totalProperty = addresses.reduce(
    (total, address) => total + parseCoverage(address.bpp_value),
    0
  );
  const totalBiIncludingEe = addresses.reduce(
    (total, address) => total + parseCoverage(address.bi_incl_ee_value),
    0
  );
  const totalBiExcludingEe = addresses.reduce(
    (total, address) => total + parseCoverage(address.bi_excl_ee_value),
    0
  );
  const totalTenants = addresses.reduce(
    (total, address) => total + parseCoverage(address.tenant_betterments_value),
    0
  );
  const totalSigns = addresses.reduce(
    (total, address) => total + parseCoverage(address.signs_value),
    0
  );
  const totalFurnitureFixtures = addresses.reduce(
    (total, address) =>
      total + parseCoverage(address.furniture_and_fixtures_value),
    0
  );
  const totalInventory = addresses.reduce(
    (total, address) => total + parseCoverage(address.inventory_value),
    0
  );
  const totalContractorsEquipment = addresses.reduce(
    (total, address) =>
      total + parseCoverage(address.contractors_equipment_value),
    0
  );
  const totalOutdoorPropertyAndAutos = addresses.reduce(
    (total, address) =>
      total + parseCoverage(address.outdoor_property_and_autos_value),
    0
  );

  const coverageTable = `
    <h3>Coverage Breakdown</h3>
    <table>
    <thead>
      <tr>
        <th>Coverages</th>
        <th>Total Limit of Insurance</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Building</td>
        <td>${formatAbbreviatedMoney(totalBuilding)}</td>
      </tr>
      <tr>
        <td>Business Personal Property</td>
        <td>${formatAbbreviatedMoney(totalProperty)}</td>
      </tr>
      <tr>
        <td>Tenant Improvements and Betterments</td>
        <td>${formatAbbreviatedMoney(totalTenants)}</td>
      </tr>
      <tr>
        <td>Business Income Including EE</td>
        <td>${formatAbbreviatedMoney(totalBiIncludingEe)}</td>
      </tr>
        <tr>
        <td>Business Income Excluding EE</td>
        <td>${formatAbbreviatedMoney(totalBiExcludingEe)}</td>
      </tr>
      <tr>
        <td>Signs</td>
        <td>${formatAbbreviatedMoney(totalSigns)}</td>
      </tr>
      <tr>
        <td>Furniture and Fixtures</td>
        <td>${formatAbbreviatedMoney(totalFurnitureFixtures)}</td>
      </tr>
      <tr>
        <td>Inventory</td>
        <td>${formatAbbreviatedMoney(totalInventory)}</td>
      </tr>
      <tr>
        <td>Contractor's Equipment</td>
        <td>${formatAbbreviatedMoney(totalContractorsEquipment)}</td>
      </tr>
      <tr>
        <td>Outdoor Property and Autos</td>
        <td>${formatAbbreviatedMoney(totalOutdoorPropertyAndAutos)}</td>
      </tr>
      <tr>
        <td><b>Scheduled TIV</b></td>
        <td><b>${formatAbbreviatedMoney(totalTIV)}</b></td>
      </tr>
    </tbody>
  </table>`;

  const exposureBreakdown =
    addresses.length === 0
      ? ""
      : addresses.length === 1
        ? `<br>
      <div>
      Address: ${addresses[0].street_address}, ${addresses[0].city}, ${
        addresses[0].state
      }, ${addresses[0].zip}
      </div>
      <div>
      Built in ${addresses[0].year_built ?? "Unknown"}
      </div>
      ${
        addresses[0].number_of_stories
          ? `<div>${addresses[0].number_of_stories} stor${
              addresses[0].number_of_stories == "1" ? "y" : "ies"
            }</div>`
          : ""
      }
      <div>
      ${addresses[0].construction_description ?? "Unknown"} Construction
      </div>
      `
        : `<br>
      <div>
      <b>${addresses.length} ${capitalize(
        buildingOrLocation
      )}s Breakdown by TIV</b>
      </div>
      <div>
      States: ${breakDownAddressFieldByTiv("state", addresses, totalTIV)}
      </div>
      <div>
      Year Built: ${breakDownAddressFieldByTiv(
        "year_built",
        addresses,
        totalTIV
      )}
      </div>
      <div>
      Construction: ${breakDownAddressFieldByTiv(
        "construction_description",
        addresses,
        totalTIV
      )}
      </div>
      <div>
      Num Stories: ${breakDownAddressFieldByTiv(
        "number_of_stories",
        addresses,
        totalTIV
      )}
      </div>`;

  return `
  <div>
  <div>
  Hello,
  <br>
  <br>
  </div>
  <div>
  This is a referral for  <b>${companyName}</b> with an effective date of <b>${
    report.company_info.effective_date
  }</b>. There ${
    locationCount === 1 ? "is 1 location" : `are ${locationCount} locations`
  } with a total TIV of <b>${formatAbbreviatedMoney(
    totalTIV
  )}</b>. ${locationsOverTenMil} ${buildingOrLocation}${
    locationsOverTenMil === 1 ? "" : "s"
  } have a TIV over 10M.
  </div>
  <h3>Referral Triggers</h3>
  <div><i>What is causing you to write this referral?</i></div>
  <h3>Proposing</h3>
  <div>Rate: </div>
  <div>Premium: </div>
  <div>AOP: </div>
  <h3>Exposure Overview</h3>
  ${windzoneAndAggregationString}
  ${squareFootageString}
  ${exposureBreakdown}
  <h3>Broker</h3>
  <div>
    The broker on this policy is <b>${
      specialtyPropertyInfo.broker_contact?.full_name ??
      specialtyPropertyInfo.report_json.company_info.broker_agent_name ??
      "unknown"
    }</b> at <b>${
      specialtyPropertyInfo.broker_contact?.organization_name ??
      specialtyPropertyInfo.report_json.company_info.broker_name ??
      "unknown"
    }</b>.
  </div>
  ${coverageTable}
  <h3>Loss History</h3>
  <div><i>Are there any notable losses to mention?</i></div>
  <br>
  <div>
  Looking forward to your feedback.
  </div>
  </div>
`;
};

type ReferralCardProps = {
  specialtyPropertyInfo: SafeReportResponse;
};

export const ReferralCard: React.FC<ReferralCardProps> = ({
  specialtyPropertyInfo,
}) => {
  const clientId = useClientId();
  const generateDefaultTextValue = () => {
    return generateInitialReferralValueGeneric(specialtyPropertyInfo, clientId);
  };

  const [text, setText] = useState(
    () =>
      specialtyPropertyInfo.report_json.referral_text?.text ??
      generateDefaultTextValue()
  );

  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";

  const toast = useToast();
  const updateReport = useUpdateReport(specialtyPropertyInfo.report_id);

  const [saveState, setSaveState] = useState<{
    dirty: boolean;
    lastSaveTime: number | undefined;
  }>({ dirty: false, lastSaveTime: undefined });

  const saveIcon = (
    <span
      className={
        updateReport.isPending
          ? "pi pi-spin pi-spinner"
          : saveState.dirty
            ? "pi pi-file-edit"
            : "pi pi-check"
      }
      style={{ fontSize: "0.75rem" }}
    />
  );
  const saveMessage = updateReport.isPending
    ? "Saving..."
    : saveState.dirty
      ? "Unsaved changes"
      : saveState.lastSaveTime
        ? "Saved at " + new Date(saveState.lastSaveTime).toLocaleTimeString()
        : "Saved (No changes)";

  const saveStatus = (
    <span
      style={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        fontSize: "12px",
      }}
    >
      {saveIcon} {saveMessage}
    </span>
  );

  const debouncedUpdate = useCallback(
    debounce((content) => {
      updateReport.mutateAsync(
        { referralText: { text: content } },
        {
          onSuccess: () =>
            setSaveState({ dirty: false, lastSaveTime: Date.now() }),
        }
      );
    }, 1000),
    []
  );

  return (
    <>
      <Card title="Referral" subTitle={saveStatus}>
        <Editor
          value={text}
          init={{
            ...{
              height: 500,
              menubar: false,
              plugins: [
                "advlist",
                "autolink",
                "lists",
                "link",
                "charmap",
                "table",
                "help",
                "wordcount",
              ],
              toolbar:
                "bold italic underline | table | numlist bullist | customcopy customdownload clearedits",
              setup: (editor) => {
                editor.ui.registry.addButton("clearedits", {
                  icon: "reload",
                  tooltip: "Clear all Edits",
                  onAction: async () => {
                    updateReport.mutateAsync(
                      {
                        referralText: { text: null },
                      },
                      {
                        onSuccess: () =>
                          setSaveState({
                            dirty: false,
                            lastSaveTime: Date.now(),
                          }),
                      }
                    );

                    setText(generateDefaultTextValue());
                  },
                });
                editor.ui.registry.addButton("customdownload", {
                  icon: "export-pdf",
                  tooltip: "Download as PDF",
                  onAction: async () => {
                    const html = editor.getContent({ format: "html" });
                    toast.show({
                      severity: "info",
                      summary: "Generating report",
                      detail: "Adding screenshots...",
                      icon: "pi pi-spin pi-spinner",
                    });
                    try {
                      const blob = await generateUnderwritingPdf(
                        specialtyPropertyInfo.report_id,
                        html
                      );
                      downloadBlob(
                        specialtyPropertyInfo.report_json.company_info
                          .company_name,
                        blob
                      );
                      toast.replace({
                        severity: "success",
                        summary: "Report downloaded",
                        detail: "You can find the report in your downloads.",
                        life: 5000,
                      });
                    } catch {
                      toast.replace({
                        severity: "error",
                        summary: "Something went wrong",
                        life: 5000,
                      });
                    }
                  },
                });
                editor.ui.registry.addButton("customcopy", {
                  icon: "copy",
                  tooltip: "Copy content",
                  onAction: async () => {
                    const content = editor.getContent({ format: "html" });
                    const textContent = editor.getContent({ format: "text" });
                    const htmlBlob = new Blob([content], { type: "text/html" });
                    const plainBlob = new Blob([textContent], {
                      type: "text/plain",
                    });

                    try {
                      await navigator.clipboard.write([
                        new ClipboardItem({
                          "text/html": htmlBlob,
                          "text/plain": plainBlob,
                        }),
                      ]);
                    } catch (err) {
                      // Fallback to text-only if clipboard.write not supported
                      navigator.clipboard.writeText(content);
                    }

                    toast.show({
                      severity: "success",
                      summary: "Copied",
                      detail: "Message copied to clipboard",
                      life: 5000,
                    });
                  },
                });
              },
              content_style:
                'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px }',
            },
            ...(isLightMode ? {} : { skin: "oxide-dark", content_css: "dark" }),
          }}
          onInit={(_, editor) => {
            editor.on("input", () => {
              // Only fires on user input
              const content = editor.getContent();
              setSaveState((x) => ({ ...x, dirty: true }));
              debouncedUpdate(content);
            });
          }}
          onEditorChange={(content) => {
            setText(content);
          }}
        />
      </Card>
    </>
  );
};
